import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { LeaveService } from "@/services/Leave/LeaveSetting"
import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Eye, Plus, Pencil, Trash, TriangleAlert } from "lucide-react"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { ILeave } from "@/interface/leave"
import moment from "moment"
import { useAppSelector } from "@/store/store"
import CommonFilter from "@/components/common/CommonFilter"
import type { IFilter } from "@/interface/common.interface"
import { GetFilterUrl, showToastNotification } from "@/utility/helper"

const DURATION_OPTIONS = [
    { label: "Full Day", value: "FULL_DAY" },
    { label: "Half Day", value: "HALF_DAY" },
    { label: "First Half", value: "FIRST_HALF" },
    { label: "Second Half", value: "SECOND_HALF" },
];

const MyLeave: React.FC = () => {
    const navigate = useNavigate()
    const loggedInUser = useAppSelector((state) => state.user.user)
    const [leaves, setLeaves] = useState<ILeave[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    // Unified filter state
    const [filter, setFilter] = useState<IFilter>({
        search: "",
        startDate: "",
        endDate: "",
        duration: "",
    });

    const toggleDeleteDialog = useCallback((id: string = "") => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen((prev) => !prev);
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
    }, []);


    const handleFilterChange = useCallback((value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
    }, []);

    const handleGetLeaves = useCallback(async () => {
        setIsLoading(true);
        try {
            // Get formatted filter string if required for API query
            const filterQuery = GetFilterUrl(filter);
            console.log("filterQuery", filterQuery)

            const response = await LeaveService.getLeaves(filterQuery);
            if (response?.status) {
                const allLeaves = response?.data?.leaves || response?.data || [];
                const userLeaves = Array.isArray(allLeaves)
                    ? allLeaves.filter((l: ILeave) => !loggedInUser?.id || l.userId === loggedInUser.id)
                    : [];
                setLeaves(userLeaves);
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false);
        }
    }, [filter, loggedInUser?.id]);

    useEffect(() => {
        handleGetLeaves();
    }, [handleGetLeaves]);

    const handleDeleteLeave = useCallback(async () => {
        try {
            const response = await LeaveService.deleteLeave(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message || "Leave deleted successfully");
                toggleDeleteDialog("");
                handleGetLeaves();
            } else {
                showToastNotification("danger", response?.message || "Failed to delete leave");
            }
        } catch (error) {
            ThrowError(error);
        }
    }, [deleteId, handleGetLeaves, toggleDeleteDialog]);
    const columns: TableColumn<ILeave>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: ILeave, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'leaveType',
            header: 'Leave Type',
            render: (item: ILeave) => (
                <span className="font-semibold capitalize">{item?.leaveType || "—"}</span>
            )
        },
        {
            key: 'startDate',
            header: 'Start Date',
            render: (item: ILeave) => (
                <span className="font-medium">
                    {item?.startDate ? moment(item.startDate).format("DD-MM-YYYY") : "—"}
                </span>
            )
        },
        {
            key: 'endDate',
            header: 'End Date',
            render: (item: ILeave) => (
                <span className="font-medium">
                    {item?.endDate ? moment(item.endDate).format("DD-MM-YYYY") : "—"}
                </span>
            )
        },
        {
            key: 'duration',
            header: 'Duration',
            render: (item: ILeave) => (
                <span className="font-medium capitalize text-slate-600">
                    {item?.duration ? item.duration.replace(/_/g, " ") : "—"}
                </span>
            )
        },
        {
            key: 'numberOfDays',
            header: 'Days',
            render: (item: ILeave) => (
                <span className="font-medium">{item?.numberOfDays || 0}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: ILeave) => {
                const status = (item?.status || "pending").toLowerCase();
                let statusClasses = "text-amber-600 border-amber-600 bg-amber-50";
                if (status === "approved" || status === "active") {
                    statusClasses = "text-green-600 border-green-600 bg-green-50";
                } else if (status === "rejected" || status === "cancelled") {
                    statusClasses = "text-destructive border-destructive bg-destructive/10";
                }
                return (
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full capitalize ${statusClasses}`}>
                        {status}
                    </span>
                )
            }
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: ILeave) => {
                const isPending = (item?.status || "pending").toLowerCase() === "pending";
                return (
                    <div className="flex gap-2">
                        <Button
                            size={'icon'}
                            className="bg-primary/10 text-primary hover:bg-primary/20"
                            title="View Details"
                            onClick={() => { if (item?.id) navigate(`${RoutesLink.leaveDetail}/${item.id}`); }}
                        >
                            <Eye size={18} />
                        </Button>
                        {isPending && (
                            <>
                                <Button
                                    size={'icon'}
                                    className="bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200"
                                    title="Edit Leave"
                                    onClick={() => { if (item?.id) navigate(`${RoutesLink.editLeave}/${item.id}`); }}
                                >
                                    <Pencil size={16} />
                                </Button>
                                <Button
                                    size={'icon'}
                                    className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                                    title="Delete Leave"
                                    onClick={() => { if (item?.id) toggleDeleteDialog(item.id); }}
                                >
                                    <Trash size={16} />
                                </Button>
                            </>
                        )}
                    </div>
                );
            }
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="My Leaves"
                    buttonText="Apply Leave"
                    buttonIcon={<Plus size={18} />}
                    isBackButtonVisible={true}
                    onClick={() => navigate(RoutesLink.addLeave)}
                />

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-visible relative z-10">
                    <CommonFilter
                        searchValue={filter.search || ""}
                        onSearchChange={handleSearchChange}
                        startDate={filter.startDate || ""}
                        endDate={filter.endDate || ""}
                        onDateRangeChange={handleDateRangeChange}
                        value={filter}
                        filterOptions={DURATION_OPTIONS}
                        filterName="duration"
                        filterPlaceholder="Select Duration"
                        onChange={handleFilterChange}
                    />
                    <CommonTable
                        columns={columns}
                        data={leaves}
                        loading={isLoading}
                    />
                </div>

                <CommonModal
                    show={deleteDialogOpen}
                    onClose={() => toggleDeleteDialog("")}
                    title=""
                    className="max-w-md rounded-2xl p-0 overflow-hidden"
                >
                    <div className="p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                                <TriangleAlert className="text-red-600 w-8 h-8" />
                            </div>
                            <h2 className="mt-5 text-2xl font-bold text-gray-900">Delete Applied Leave?</h2>
                            <p className="mt-3 text-sm leading-6 text-gray-500">
                                This action cannot be undone. Once deleted, the leave application will be permanently removed.
                            </p>
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => toggleDeleteDialog("")}>
                                Cancel
                            </Button>
                            <Button variant="destructive" className="flex-1" onClick={handleDeleteLeave}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </CommonModal>
            </div>
        </Layout>
    )
}

export default MyLeave